import { AnyError, FormMethods, useDebouncedValue, useFormContainerContext } from '@if/mui'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import { CalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useCallback, useEffect, useRef } from 'react'
import { FieldValues, Path, PathValue } from 'react-hook-form'
import { CALENDAR_PREVIEW_FIELD, CalendarPreviewFormValues } from './LoanCalendarActionPreview-schema'

/**
 * How long the fields have to settle before the calendar is recomputed. One delay for every
 * calendar dialog — each of them used to pick its own, from a bare next tick to a full
 * second, and the agent had no way to tell which one they were waiting on.
 */
export const CALENDAR_PREVIEW_DEBOUNCE_MS = 400

/** Registered on the form while no settled preview matches the values on screen. */
const PREVIEW_BUSY_KEY = 'calendarPreview'

/**
 * A field's value once it has stopped moving, or `undefined` while it is still settling —
 * so an action built out of these is never a mix of one field's new value and another's old
 * one. It also keeps the recompute honest against `isValid`, which always describes what is
 * on screen: only when every field has caught up do the two agree on the same calendar.
 *
 * `undefined` doubles as "no value yet", which costs nothing: a field the agent has not
 * filled leaves the form invalid, and there is nothing to preview either way.
 */
export const useSettledValue = <T>(value: T): T | undefined => {
  const settled = useDebouncedValue(value, CALENDAR_PREVIEW_DEBOUNCE_MS)
  return Object.is(settled, value) ? settled : undefined
}

interface UseLoanCalendarActionPreviewProps<TFieldValues extends FieldValues & CalendarPreviewFormValues> {
  form: FormMethods<TFieldValues>
  /** The calendar the dialog opened on. Every preview is computed from it, never from the last one. */
  initialSignedSimulation: SignedCalendarSimulation
  /**
   * The action to preview, rebuilt from the settled (debounced) values, or `null` while the
   * fields cannot form one. Memoize it — its identity is what triggers a recompute.
   */
  action: CalendarSimulationAction | null
}

/**
 * Keeps `previewedSimulation` in step with the form: cleared the instant any field moves,
 * recomputed once the settled values form an action. The preview is the only thing Confirm
 * ever applies, so the field being null *is* the "not ready" state — a dialog gates Confirm
 * on it rather than on a request in flight.
 *
 * Returns the recompute failure, for the dialog to surface next to the preview.
 */
export const useLoanCalendarActionPreview = <TFieldValues extends FieldValues & CalendarPreviewFormValues>({
  form,
  initialSignedSimulation,
  action,
}: UseLoanCalendarActionPreviewProps<TFieldValues>): AnyError => {
  const { loan } = initialSignedSimulation.simulation.fullLoan
  const { mutateAsync: computeAction, error } = useComputeCalendarAction(loan.id, loan.financingCompanyId)
  const { setBusy } = useFormContainerContext()

  const setPreview = useCallback(
    (preview: SignedCalendarSimulation | null) => {
      // TFieldValues is constrained to carry the field, but the constraint does not reach
      // react-hook-form's Path / PathValue machinery.
      form.setValue(
        CALENDAR_PREVIEW_FIELD as Path<TFieldValues>,
        preview as PathValue<TFieldValues, Path<TFieldValues>>,
      )
      // The form's own busy blocker — and what re-renders the dialog footer: the Confirm gate
      // reads the preview off the form, and only a form-context change gets it read again.
      setBusy(PREVIEW_BUSY_KEY, preview === null)
    },
    [form, setBusy],
  )

  // Bumped by anything that makes an answer still in flight obsolete: an edit, a newer request,
  // an unmount. A request writes its result only while the counter still reads what it took.
  const requestRef = useRef(0)

  // Any edit drops the preview the moment it lands, so Confirm closes before the debounce has
  // even started — the calendar on offer is never one computed from older values.
  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ name }) => {
        if (name && name !== CALENDAR_PREVIEW_FIELD) {
          requestRef.current += 1
          setPreview(null)
        }
      },
    })
    return unsubscribe
  }, [form, setPreview])

  useEffect(() => {
    if (!action) return
    const request = (requestRef.current += 1)
    void computeAction({ ...initialSignedSimulation, action })
      .then((preview) => {
        if (requestRef.current === request) setPreview(preview)
      })
      // The failure is reported through the mutation's own `error`; catching it here only
      // keeps the rejection from escaping as an unhandled one.
      .catch(() => undefined)
    return () => {
      requestRef.current += 1
    }
  }, [action, computeAction, initialSignedSimulation, setPreview])

  return error
}
